/**
 * 加解密方法
 * 1.加解密传输方法
 * 2.加解密缓存方法
 * Copyright (c) 2025 dfec
 */
import { GmCrypto } from '@fanghub/tool-crypto'
import type { PluginCache, Plugin } from '@fanghub/tool-cache'
import { LocalCache, SessionCache, CookieCache } from '@fanghub/tool-cache'
// 先获取随机密钥和IV
const keyPair = GmCrypto.generateSM4KeyPair()
const localCache: PluginCache<string | null> = new LocalCache<string | null>()
const sessionCache: PluginCache<string | null> = new SessionCache<string | null>()
const cookieCache: PluginCache<string | null> = new CookieCache<string | null>()
// 定义加密插件
const encryptionPlugin: Plugin<string | null> = {
    // 包装方法
    wrapper(value: string | null): string | null {
        // 获取国密加密算法的key和iv
        const keyPair = GmCrypto.generateSM4KeyPair()
        // 加密逻辑
        const encodeData = GmCrypto.sm4Encrypt({
            data: JSON.stringify(value),
            key: keyPair.key,
            iv: keyPair.iv,
            mode: 'cbc'
        })
        return `${keyPair.key}${keyPair.iv}${encodeData}`
    },
    // 解包方法
    unwrapper(value: string): string | null {
        const key = value.substring(0, 32)
        const iv = value.substring(32, 64)
        const encodeData = value.substring(64)
        // 解密逻辑
        const result = GmCrypto.sm4Decrypt({
            cipherText: encodeData,
            key,
            iv,
            mode: 'cbc'
        })
        return result
    }
}
localCache.addPlugins(encryptionPlugin)
sessionCache.addPlugins(encryptionPlugin)
cookieCache.addPlugins(encryptionPlugin)
/**
 * sessionStorage 加解密缓存
 */
const EncryptSessionCache = class {
    /**
     * 加密缓存
     * @param {string} key 缓存key
     * @param {string} value 缓存value
     */
    setItem<T>(key: string, value: T): void {
        if (key !== null && value !== null) {
            sessionCache.setItem(key, JSON.stringify(value))
        }
    }

    /**
     * 获取值
     * @param {string} key
     * @returns
     */
    getItem<T>(key: string): T | null {
        const value = sessionCache.getItem(key)
        if (value === null || value === undefined) return null
        try {
            const parsed = typeof value === 'string' ? JSON.parse(value) : value
            return parsed as T
        } catch {
            return value as T
        }
    }

    /**
     * 删除值
     * @param {string} key
     * @returns
     */
    removeItem(key: string): void {
        sessionCache.removeItem(key)
    }

    /**
     * 清空值
     * @returns
     */
    clear(): void {
        sessionCache.clear()
    }
}
/**
 * localStorage 加解密缓存
 */
const EncryptLocalCache = class {
    /**
     * 加密缓存
     * @param {string} key 缓存key
     * @param {string} value 缓存value
     */
    setItem<T>(key: string, value: T): void {
        if (key !== null && value !== null) {
            localCache.setItem(key, value as string)
        }
    }

    /**
     * 获取值
     * @param {string} key
     * @returns
     */
    getItem<T>(key: string): T | null {
        const value = localCache.getItem(key)

        if (value === null || value === undefined) return null
        try {
            const parsed = typeof value === 'string' ? JSON.parse(value) : value
            return parsed as T
        } catch {
            return value as T
        }
    }

    /**
     * 删除值
     * @param {string} key
     * @returns
     */
    removeItem(key: string): void {
        localCache.removeItem(key)
    }

    /**
     * 清空值
     * @returns
     */
    clear(): void {
        localCache.clear()
    }
}
/**
 * cookie 加解密缓存
 */
const EncryptCookieCache = class {
    /**
     * 加密缓存
     * @param {string} key 缓存key
     * @param {string} value 缓存value
     */
    setItem<T>(key: string, value: T): void {
        if (key !== null && value !== null) {
            cookieCache.setItem(key, JSON.stringify(value))
        }
    }

    /**
     * 获取值
     * @param {string} key
     * @returns
     */
    getItem<T>(key: string): T | null {
        const value = cookieCache.getItem(key)
        if (value === null || value === undefined) return null
        try {
            const parsed = typeof value === 'string' ? JSON.parse(value) : value
            return parsed as T
        } catch {
            return value as T
        }
    }

    /**
     * 删除值
     * @param {string} key
     * @returns
     */
    removeItem(key: string): void {
        cookieCache.removeItem(key)
    }

    /**
     * 清空值
     * @returns
     */
    clear(): void {
        cookieCache.clear()
    }
}
/**
 * @description 加密方法
 * @param encodedText 需要加密的文本
 * @returns 加密后的文本
 */
const encode = (encodedText: string): string => {
    // 加密逻辑
    const encodeData = GmCrypto.sm4Encrypt({
        data: encodedText,
        key: keyPair.key,
        iv: keyPair.iv,
        mode: 'cbc'
    })
    return encodeBy(encodeData, keyPair.key, keyPair.iv)
}
/**
 * @description 解密方法
 * @param encodedText 需要解密的文本
 * @returns 解密后的文本
 */
const decode = (encodedText: string): string => {
    const dataObj = decodeBy(encodedText)
    // 解密逻辑
    const decodeData = GmCrypto.sm4Decrypt({
        cipherText: dataObj.data,
        key: dataObj.key,
        iv: dataObj.iv,
        mode: 'cbc'
    })
    return decodeData
}

/**
 * @description 加密前置方法
 * @param encodedText 需要加密的文本
 * @param key 密钥
 * @param iv 初始向量
 * @returns 加密后的文本
 */
const encodeBy = (encodedText: string, key: string, iv: string): string => {
    const keyArray = hexStringToByteArray(key)
    const ivArray = hexStringToByteArray(iv)
    const binaryString = atob(encodedText)
    const bytes = new Int8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
    }
    const result = new Int8Array(bytes.length + keyArray.length + ivArray.length)
    result.set(keyArray, 0)
    result.set(bytes, keyArray.length)
    result.set(ivArray, keyArray.length + bytes.length)
    return int8ArrayToBase64(result)
}
/**
 * @description 解密前置方法
 * @param encodedText 需要解密的文本
 * @returns 解密后的文本
 */
const decodeBy = (encodedText: string): { data: string; iv: string; key: string } => {
    const encodeData = atob(encodedText)
    const byteArray = new Int8Array(encodeData.length)
    for (let i = 0; i < encodeData.length; i++) {
        byteArray[i] = encodeData.charCodeAt(i)
    }
    const secretKey = byteArray.slice(0, 16)
    const data = byteArray.slice(16, -16)
    const iv = byteArray.slice(-16)
    const dataBase64 = int8ArrayToBase64(data)
    const ivBase64 = int8ArrayToHex(iv)
    const keyBase64 = int8ArrayToHex(secretKey)
    return {
        data: dataBase64,
        iv: ivBase64,
        key: keyBase64
    }
}

/**
 * 16进制字符串转字节数组
 */
const hexStringToByteArray = (hexString: string) => {
    const byteArray = new Int8Array(hexString.length / 2)
    for (let i = 0; i < hexString.length; i += 2) {
        byteArray[i / 2] = Number.parseInt(hexString.slice(i, i + 2), 16)
    }
    return byteArray
}

/**
 * int 转base 64
 */
const int8ArrayToBase64 = (int8Array: Int8Array) => {
    // 创建Uint8Array视图以正确读取字节
    const uint8Array = new Uint8Array(int8Array.buffer, int8Array.byteOffset, int8Array.length)
    // 分块处理大数组以避免参数溢出
    const chunkSize = 0x8000 // 32768字节的块大小
    let binaryString = ''
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, uint8Array.length)) as unknown as number[]
        binaryString += String.fromCharCode.apply(null, chunk)
    }
    // 转换为Base64
    return btoa(binaryString)
}

/**
 * int 转16进制
 */
const int8ArrayToHex = (int8Array: Int8Array) => {
    return Array.from(int8Array)
        .map(
            b =>
                (b & 0xFF)
                    .toString(16) // 处理负值（转换为无符号）
                    .padStart(2, '0') // 补零对齐（如 `a` → `0a`）
        )
        .join('')
}
export { decode, encode, EncryptCookieCache, EncryptLocalCache, EncryptSessionCache }
